// วิเคราะห์ข้อสอบ — ความยาก (p) อำนาจจำแนก (r) และการกระจายของคำตอบรายข้อ
//
// ตัวเลขคำนวณที่ server (utils/examScoring.analyze) ตามขอบเขตที่เลือก (ห้องเดียว/ทุกห้อง)
// ธงที่สำคัญที่สุดคือ r ติดลบ ซึ่งส่วนใหญ่แปลว่าเฉลยผิด — จึงมีปุ่มเปลี่ยนเฉลยให้กดได้จากตรงนี้เลย
import React, { useEffect, useState } from 'react'
import { Alert, Badge, Button, Col, OverlayTrigger, Row, Table, Tooltip } from 'react-bootstrap'
import Swal from 'sweetalert2'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { API, CHOICE_LABELS, FLAG_INFO, answerLabel, fmt, keyLabel, showError } from './examUtils'

const Stat = ({ label, value }) => (
  <Col xs={4} md={2}>
    <div className="border rounded p-2 text-center h-100">
      <div className="fs-xs text-secondary">{label}</div>
      <div className="fs-5 fw-semibold">{value}</div>
    </div>
  </Col>
)

// แถบการกระจายของคำตอบ ช่องเฉลยเป็นสีเขียว
const Distribution = ({ item, choices }) => {
  const options = item.eq_type === 'truefalse'
    ? ['T', 'F']
    : CHOICE_LABELS.slice(0, choices).map((_, i) => String(i + 1))
  const keys = item.eq_key && item.eq_key !== '*' ? item.eq_key.split(',') : []
  const max = Math.max(1, ...options.map((o) => item.dist?.[o] || 0), item.blank || 0)
  return (
    <div className="d-flex align-items-end gap-1" style={{ height: 34 }}>
      {options.map((o) => {
        const n = item.dist?.[o] || 0
        const isKey = keys.includes(o)
        return (
          <div key={o} className="text-center" style={{ width: 24 }}>
            <div className="fs-xs">{n}</div>
            <div
              className={isKey ? 'bg-success' : o === item.suspectKey ? 'bg-danger' : 'bg-secondary bg-opacity-50'}
              style={{ height: Math.max(2, (n / max) * 16), borderRadius: 2 }}
            />
            <div className={`fs-xs ${isKey ? 'fw-bold text-success' : ''}`}>{answerLabel(o)}</div>
          </div>
        )
      })}
      {(item.blank > 0 || item.multi > 0) && (
        <div className="fs-xs text-secondary ms-1 align-self-center">
          {item.blank > 0 && <div>ไม่ตอบ {item.blank}</div>}
          {item.multi > 0 && <div>หลายช่อง {item.multi}</div>}
        </div>
      )}
    </div>
  )
}

export default function ExamAnalysis({ examId, exam, questions, teachId, version, onChanged }) {
  const { setLoading } = useLoading()
  const [analysis, setAnalysis] = useState(null)

  useEffect(() => {
    let alive = true
    axiosInstance.get(`${API}/exams/${examId}/results`, { params: teachId ? { teach_id: teachId } : {} })
      .then((res) => alive && setAnalysis(res.data.analysis))
      .catch((err) => showError(err, 'โหลดผลวิเคราะห์ไม่สำเร็จ'))
    return () => { alive = false }
  }, [examId, teachId, version])

  const changeKey = async (eqNo, newKey) => {
    const ok = await Swal.fire({
      icon: 'question',
      title: `เปลี่ยนเฉลยข้อ ${eqNo} เป็น ${keyLabel(newKey)}?`,
      text: 'คะแนนของทุกคนทุกห้องคิดใหม่ทันที ถ้าส่งคะแนนเข้าตารางคะแนนไปแล้วต้องส่งใหม่',
      showCancelButton: true,
      confirmButtonText: 'เปลี่ยนเฉลย',
      cancelButtonText: 'ยกเลิก',
    })
    if (!ok.isConfirmed) return
    setLoading(true)
    try {
      await axiosInstance.put(`${API}/exams/${examId}`, {
        exam_name: exam.exam_name,
        exam_choices: exam.exam_choices,
        questions: questions.map((q) => ({
          eq_type: q.eq_type,
          eq_points: q.eq_points,
          eq_key: q.eq_no === eqNo ? newKey : q.eq_key,
        })),
      })
      await onChanged()
    } catch (err) {
      showError(err, 'เปลี่ยนเฉลยไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  if (!analysis) return <div className="text-secondary">กำลังโหลด…</div>
  const { summary, items } = analysis
  if (!summary.n) return <div className="text-secondary text-center py-4">ยังไม่มีกระดาษคำตอบในขอบเขตนี้</div>

  const flagged = items.filter((it) => it.flags.includes('negative') || it.flags.includes('distractor') || it.flags.includes('nokey'))

  return (
    <>
      <Row className="g-2 mb-3">
        <Stat label="จำนวนคน" value={summary.n} />
        <Stat label={`เฉลี่ย (เต็ม ${fmt(summary.total_points)})`} value={fmt(summary.mean)} />
        <Stat label="ส่วนเบี่ยงเบน" value={fmt(summary.sd)} />
        <Stat label="มัธยฐาน" value={fmt(summary.median)} />
        <Stat label="ต่ำสุด" value={fmt(summary.min)} />
        <Stat label="สูงสุด" value={fmt(summary.max)} />
      </Row>

      {flagged.length > 0 && (
        <Alert variant="danger" className="fs-sm py-2">
          ควรตรวจเฉลย {flagged.length} ข้อ: {flagged.map((it) => it.eq_no).join(', ')}
          {' '}— คนคะแนนสูงตอบข้อเหล่านี้ไม่ตรงเฉลย หรือยังไม่มีเฉลย
        </Alert>
      )}
      {!summary.group_size && (
        <Alert variant="secondary" className="fs-sm py-2">
          ไม่ถึง 10 คน จึงยังไม่คิดอำนาจจำแนก (กลุ่มสูง/ต่ำเล็กเกินกว่าตัวเลขจะมีความหมาย)
        </Alert>
      )}

      <div className="table-responsive">
        <Table size="sm" className="align-middle mb-0">
          <thead>
            <tr>
              <th style={{ width: 45 }}>ข้อ</th>
              <th style={{ width: 70 }}>เฉลย</th>
              <th style={{ width: 80 }} className="text-end">ตอบถูก (p)</th>
              <th style={{ width: 80 }} className="text-end">จำแนก (r)</th>
              <th>การกระจายของคำตอบ</th>
              <th>ข้อสังเกต</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.eq_no} className={it.flags.includes('negative') ? 'table-danger' : undefined}>
                <td className="fw-semibold">{it.eq_no}</td>
                <td>{it.eq_type === 'written' ? <span className="text-secondary fs-sm">อัตนัย</span> : keyLabel(it.eq_key) || '-'}</td>
                <td className="text-end">{it.p === null ? '-' : `${Math.round(it.p * 100)}%`}</td>
                <td className="text-end">{fmt(it.r)}</td>
                <td>
                  {it.eq_type === 'written'
                    ? <span className="fs-sm text-secondary">เฉลี่ย {fmt(it.mean)} / {fmt(it.eq_points)} · ตรวจแล้ว {it.graded} คน</span>
                    : <Distribution item={it} choices={exam.exam_choices} />}
                </td>
                <td>
                  <div className="d-flex flex-wrap gap-1 align-items-center">
                    {it.flags.map((f) => (
                      <OverlayTrigger key={f} overlay={<Tooltip>{FLAG_INFO[f]?.help}</Tooltip>}>
                        <Badge bg={FLAG_INFO[f]?.variant || 'secondary'}>{FLAG_INFO[f]?.label || f}</Badge>
                      </OverlayTrigger>
                    ))}
                    {it.suspectKey && (
                      <Button size="sm" variant="outline-danger" className="py-0" onClick={() => changeKey(it.eq_no, it.suspectKey)}>
                        ใช้ {answerLabel(it.suspectKey)} เป็นเฉลย
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div className="fs-xs text-secondary mt-2">
        p = สัดส่วนคนที่ตอบถูก (0.2–0.8 กำลังดี) · r = p ของกลุ่มคะแนนสูง 27% ลบกลุ่มคะแนนต่ำ 27% (ตั้งแต่ 0.2 ขึ้นไปใช้ได้, ติดลบ = ตรวจเฉลย)
      </div>
    </>
  )
}
